// Suggested moves (Workload Intelligence Phase 3) — the server proposes work
// items that could move from a heavily loaded plan to a candidate with room.
// PROPOSALS ONLY: nothing changes until the user reassigns through the
// existing ReassignDialog (POST /work-items/{id}/reassign), opened prefilled
// with the item + candidate and the reason "Workload rebalancing". Copy
// describes the work and the plan ("Very high workload", "headroom 9"), never
// the person; a percentage appears only for a configured capacity (Phase 2).
// The panel hides itself on 403 (no team reach) and collapses when empty.
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, ArrowRightLeft, ChevronDown, Lightbulb } from 'lucide-react';
import { Button } from '@/features/shared/components/ui/button';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/features/shared/components/ui/collapsible';
import type { Person } from '@/features/shared/components/PeoplePicker';
import { getApiErrorMessage } from '@/lib/apiError';
import { useEligibleAssignees } from '@/features/work/api/hooks';
import { useWorkloadSuggestions } from '@/features/work/api/workloadHooks';
import type { SuggestionCandidate, SuggestionPerson, WorkloadSuggestion } from '@/services/workloadService';
import { ReassignDialog } from '../shared';
import { bandLabel } from './health';

type T = (key: string, opts?: Record<string, unknown>) => string;

export const DEFAULT_REBALANCE_REASON_KEY = 'sugg_reason_default';

const httpStatus = (e: unknown): number | undefined => (e as { response?: { status?: number } } | null)?.response?.status;

const fmtDue = (v?: string | null): string => {
  if (!v) return '';
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? v : d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

/** "Very high workload · 150%" — the percentage ONLY when capacity is configured. */
export function personLoadLabel(t: T, p: SuggestionPerson): string {
  const band = bandLabel(t, p.band);
  return p.capacity ? `${band} · ${Math.round(p.capacity.utilization_pct)}%` : band;
}

/** "Low workload · headroom 9" — headroom only when capacity is configured. */
export function candidateLoadLabel(t: T, c: SuggestionCandidate): string {
  const band = bandLabel(t, c.band);
  return c.capacity ? `${band} · ${t('sugg_headroom', { defaultValue: 'headroom {{count}}', count: c.capacity.headroom_units })}` : band;
}

const toPerson = (c: SuggestionCandidate): Person => ({
  employee_id: c.employee_id, display_name: c.display_name, job_title: c.job_title, department_name: c.department_name,
});

export const SuggestionsPanel: React.FC<{
  departmentId?: string;
  refreshKey?: number;
  /** Fired after a successful reassignment so the parent can refetch people/summary. */
  onChanged?: () => void;
}> = ({ departmentId, refreshKey = 0, onChanged }) => {
  const { t } = useTranslation('work');
  const tt = t as unknown as T;
  const [bump, setBump] = useState(0);
  const { data, loading, error } = useWorkloadSuggestions({ department_id: departmentId || undefined, limit: 10 }, true, refreshKey + bump);
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState<{ s: WorkloadSuggestion; c: SuggestionCandidate } | null>(null);
  const fetchAssignees = useEligibleAssignees('assign', { workItemId: sel?.s.item.id });
  // Default open when there is something to show; re-evaluated per payload.
  useEffect(() => { if (data) setOpen(data.items.length > 0); }, [data]);

  if (httpStatus(error) === 403) return null;
  if (loading && !data) return null;
  if (error) {
    return (
      <p className="text-xs text-muted-foreground" data-testid="suggestions-error">
        {getApiErrorMessage(error, t('sugg_load_failed', { defaultValue: 'Could not load suggestions.' }))}
      </p>
    );
  }
  if (!data) return null;

  return (
    <Collapsible open={open} onOpenChange={setOpen}>
      <section className="rounded-[12px] border border-border" data-testid="suggestions-panel">
        <CollapsibleTrigger asChild>
          <button type="button" className="flex w-full items-start justify-between gap-3 p-3 text-left" aria-expanded={open} data-testid="suggestions-toggle">
            <span className="flex items-start gap-2 min-w-0">
              <Lightbulb className="w-4 h-4 mt-0.5 text-warning-strong shrink-0" />
              <span className="min-w-0">
                <span className="block text-sm font-medium text-charcoal">
                  {t('sugg_title', { defaultValue: 'Suggested moves' })}
                  {data.items.length > 0 && <span className="text-muted-foreground"> · {data.items.length}</span>}
                </span>
                <span className="block text-xs text-muted-foreground">{t('sugg_sub', { defaultValue: 'Suggestions only — nothing changes until you reassign.' })}</span>
              </span>
            </span>
            <ChevronDown className={`w-4 h-4 text-muted-foreground shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <div className="border-t border-border">
            {data.items.length === 0 ? (
              <p className="p-3 text-xs text-muted-foreground" data-testid="suggestions-empty">{t('sugg_none', { defaultValue: 'No suggested moves right now.' })}</p>
            ) : (
              <ul className="divide-y divide-border">
                {data.items.map((s) => {
                  const meta = [s.item.effort_size || null, s.item.planned_end_at ? tt('sugg_due', { defaultValue: 'due {{date}}', date: fmtDue(s.item.planned_end_at) }) : null].filter(Boolean).join(' · ');
                  return (
                    <li key={s.id} className="p-3 space-y-1.5" data-testid={`suggestion-${s.id}`}>
                      <p className="text-sm text-foreground">
                        {t('sugg_move', { defaultValue: 'Move' })}{' '}
                        <Link to={`/app/work/${s.item.id}`} className="font-medium hover:underline">{s.item.title}</Link>
                        {meta && <span className="text-muted-foreground"> ({meta})</span>}{' '}
                        {t('sugg_from', { defaultValue: 'from' })}{' '}
                        <span className="font-medium">{s.from.display_name}</span>
                        <span className="text-muted-foreground"> ({personLoadLabel(tt, s.from)})</span>
                      </p>
                      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                        <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" aria-hidden="true" />
                        <span className="text-xs text-muted-foreground">{t('sugg_candidates', { defaultValue: 'candidates:' })}</span>
                        {s.candidates.map((c) => (
                          <span key={c.employee_id} className="inline-flex items-center gap-1.5 rounded-full border border-border px-2 py-0.5" data-testid={`candidate-${s.id}-${c.employee_id}`}>
                            <span className="text-foreground">{c.display_name}</span>{' '}
                            <span className="text-xs text-muted-foreground">({candidateLoadLabel(tt, c)})</span>{' '}
                            <Button size="sm" variant="ghost" className="h-6 px-1.5 text-[11px] text-primary-600" onClick={() => setSel({ s, c })}
                              aria-label={tt('sugg_reassign_aria', { defaultValue: 'Reassign {{title}} to {{name}}', title: s.item.title, name: c.display_name })}>
                              <ArrowRightLeft className="w-3 h-3 mr-1" />{t('sugg_reassign', { defaultValue: 'Reassign…' })}
                            </Button>
                          </span>
                        ))}
                      </div>
                      {(s.why.length > 0 || s.candidates.some((c) => c.why.length > 0)) && (
                        <p className="text-xs text-muted-foreground" data-testid={`suggestion-why-${s.id}`}>
                          {[...s.why, ...s.candidates.flatMap((c) => c.why.map((w) => `${c.display_name}: ${w}`))].join(' · ')}
                        </p>
                      )}
                    </li>
                  );
                })}
              </ul>
            )}
            {data.note && <p className="border-t border-border px-3 py-2 text-[11px] text-muted-foreground" data-testid="suggestions-note">{data.note}</p>}
          </div>
        </CollapsibleContent>
      </section>
      {sel && (
        <ReassignDialog open workItemId={sel.s.item.id} currentOwnerId={sel.s.from.employee_id} currentOwnerName={sel.s.from.display_name}
          fetchPage={fetchAssignees} initialTarget={toPerson(sel.c)}
          initialReason={tt(DEFAULT_REBALANCE_REASON_KEY, { defaultValue: 'Workload rebalancing' })}
          onClose={() => setSel(null)}
          onDone={() => { setBump((b) => b + 1); onChanged?.(); }} />
      )}
    </Collapsible>
  );
};

export default SuggestionsPanel;
